'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { BadgeCheck, Eye, EyeOff, Share2, Loader2 } from 'lucide-react'
import ImageUpload from '../../../components/ImageUpload'
import ShareButtons from '../../../components/ShareButtons'

/* MODULE C — the Khadim's own profile manager. Private by default: the public
   achievement page and sharing affordances unlock only when the volunteer opts
   in here. */

type Standing = {
  profile: { id: string; handle: string; displayName: string; photoUrl: string | null; city: string | null; bio: string | null; isPublic: boolean; shareEnabled: boolean }
  contributions: Array<{ id: string; title: string; createdAt: string }>
  count: number
  level: { name: string }
  next: { name: string } | null
  toNext: number
  badges: Array<{ key: string; label: string }>
}

export default function VolunteerProfilePage() {
  const [st, setSt] = useState<Standing | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [authed, setAuthed] = useState(true)
  const [form, setForm] = useState({ displayName: '', handle: '', city: '', bio: '', photoUrl: '' })
  const [origin, setOrigin] = useState('')

  useEffect(() => { setOrigin(window.location.origin) }, [])

  const apply = (s: Standing) => {
    setSt(s)
    setForm({
      displayName: s.profile.displayName || '', handle: s.profile.handle || '',
      city: s.profile.city || '', bio: s.profile.bio || '', photoUrl: s.profile.photoUrl || '',
    })
  }

  useEffect(() => {
    fetch('/api/volunteer/profile')
      .then((r) => { if (r.status === 401) { setAuthed(false); return null } return r.ok ? r.json() : null })
      .then((d) => { if (d?.standing) apply(d.standing) })
      .finally(() => setLoading(false))
  }, [])

  const patch = async (body: any) => {
    setSaving(true)
    try {
      const r = await fetch('/api/volunteer/profile', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const d = await r.json().catch(() => null)
      if (d?.standing) apply(d.standing)
    } finally { setSaving(false) }
  }

  const saveDetails = () => patch(form)
  const toggle = (key: 'isPublic' | 'shareEnabled', value: boolean) => patch({ [key]: value })

  if (loading) return <main className="vp"><div className="vp-wrap"><Loader2 className="spin" /> Loading your profile…<style>{spinCss}</style></div></main>
  if (!authed) return (
    <main className="vp"><div className="vp-wrap vp-auth">
      <h1>Volunteer profile</h1>
      <p>Sign in to set up your Khadim profile, track your contributions, and share your achievements.</p>
      <Link className="vp-cta" href="/login?next=/volunteer/profile">Sign in</Link>
      <style>{baseCss}</style>
    </div></main>
  )
  if (!st) return <main className="vp"><div className="vp-wrap">Profiles are unavailable right now.<style>{baseCss}</style></div></main>

  const publicUrl = `${origin}/khadim/${st.profile.handle}`
  const shareText = `${st.profile.displayName} is a ${st.level.name} on Masjids.in — ${st.count} verified ${st.count === 1 ? 'contribution' : 'contributions'}.`

  return (
    <main className="vp">
      <div className="vp-wrap">
        <header className="vp-head">
          <span className="vp-level"><BadgeCheck size={13} strokeWidth={1.6} /> {st.level.name}</span>
          <h1>Your Khadim profile</h1>
          <p>{st.count} verified {st.count === 1 ? 'contribution' : 'contributions'}{st.next ? ` · ${st.toNext} more to ${st.next.name}` : ' · top level reached'}</p>
        </header>

        {st.badges.length > 0 && (
          <div className="vp-badges">{st.badges.map((b) => <span key={b.key}>{b.label}</span>)}</div>
        )}

        <section className="vp-card">
          <h2>Profile details</h2>
          <div className="vp-photo">
            <ImageUpload value={form.photoUrl} onChange={(url) => setForm((f) => ({ ...f, photoUrl: url }))} label="Profile photo" folder="volunteers" context="avatar" />
          </div>
          <label><span>Display name</span><input value={form.displayName} maxLength={80} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} /></label>
          <label><span>Public handle</span><input value={form.handle} maxLength={40} onChange={(e) => setForm((f) => ({ ...f, handle: e.target.value }))} /><small>masjids.in/khadim/{form.handle || 'your-handle'}</small></label>
          <label><span>City</span><input value={form.city} maxLength={80} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} /></label>
          <label><span>Short bio (optional)</span><textarea value={form.bio} maxLength={400} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} /></label>
          <button className="vp-save" onClick={saveDetails} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
        </section>

        <section className="vp-card">
          <h2>Privacy &amp; sharing</h2>
          <p className="vp-note">Your profile is private by default. Nothing is public until you turn it on.</p>
          <label className="vp-toggle">
            <input type="checkbox" checked={st.profile.isPublic} onChange={(e) => toggle('isPublic', e.target.checked)} />
            <span>{st.profile.isPublic ? <Eye size={15} /> : <EyeOff size={15} />} Make my achievement page public</span>
          </label>
          <label className="vp-toggle">
            <input type="checkbox" checked={st.profile.shareEnabled} disabled={!st.profile.isPublic} onChange={(e) => toggle('shareEnabled', e.target.checked)} />
            <span><Share2 size={15} /> Allow sharing my achievements</span>
          </label>

          {st.profile.isPublic && (
            <div className="vp-share">
              <Link className="vp-link" href={`/khadim/${st.profile.handle}`} target="_blank">View public page → {publicUrl}</Link>
              {st.profile.shareEnabled && <div className="vp-sharebtns"><ShareButtons entityId={st.profile.id} entityType="khadim_profile" title={shareText} url={`/khadim/${st.profile.handle}`} /></div>}
            </div>
          )}
        </section>

        <section className="vp-card">
          <h2>Your contributions</h2>
          {st.contributions.length ? (
            <ul className="vp-contribs">
              {st.contributions.map((c) => (
                <li key={c.id}><span>{c.title}</span><small>{new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</small></li>
              ))}
            </ul>
          ) : (
            <p className="vp-empty">No verified contributions yet. List or claim a masjid, add photos, or help with field verification — your contributions appear here as they’re recorded.</p>
          )}
        </section>
      </div>
      <style>{baseCss}</style>
      <style>{spinCss}</style>
    </main>
  )
}

const spinCss = `.spin{animation:vpspin 1s linear infinite;vertical-align:middle;margin-right:8px}@keyframes vpspin{to{transform:rotate(360deg)}}`
const baseCss = `
  .vp{background:var(--f-beige,#F6F4EE);min-height:100vh;padding:40px 0}
  .vp-wrap{max-width:680px;margin:0 auto;padding:0 22px;display:grid;gap:16px;color:var(--f-ink,#1d1c16)}
  .vp-auth{text-align:center;background:#fff;border:1px solid #e6e4dc;border-radius:18px;padding:40px 24px}
  .vp-cta{display:inline-block;margin-top:12px;background:var(--f-green,#0E7C61);color:#fff;border-radius:99px;padding:11px 22px;text-decoration:none;font-weight:700;font-size:14px}
  .vp-head h1{font-family:var(--f-display,Poppins);font-size:28px;font-weight:700;color:var(--f-navy,#101A38);margin:8px 0 4px;letter-spacing:-.01em}
  .vp-head p{font-size:13.5px;color:var(--f-slate,#64748b)}
  .vp-level{display:inline-flex;align-items:center;gap:5px;font-family:var(--f-mono,monospace);font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--f-gold-deep,#9A7B1E);background:rgba(212,175,55,.12);border-radius:99px;padding:4px 11px;font-weight:600}
  .vp-badges{display:flex;gap:8px;flex-wrap:wrap}
  .vp-badges span{font-size:11.5px;font-weight:600;color:var(--f-green-deep,#0E7C61);border:1px solid rgba(14,124,97,.35);background:rgba(14,124,97,.06);border-radius:99px;padding:5px 12px}
  .vp-card{background:#fff;border:1px solid var(--f-line,#e6e4dc);border-radius:18px;padding:22px;display:grid;gap:13px}
  .vp-card h2{font-family:var(--f-display,Poppins);font-size:16px;font-weight:600;color:var(--f-navy,#101A38);margin:0}
  .vp-note{font-size:12.5px;color:var(--f-slate,#64748b);margin:0}
  .vp-card label{display:grid;gap:5px}
  .vp-card label span{font-size:11.5px;font-weight:700;color:var(--f-slate,#64748b);text-transform:uppercase;letter-spacing:.03em}
  .vp-card input,.vp-card textarea{border:1px solid #dbe6e1;border-radius:11px;background:#fff;font:inherit;padding:10px 12px;color:#1d1c16}
  .vp-card textarea{min-height:74px;resize:vertical}
  .vp-card label small{font-size:11px;color:var(--f-slate,#8a887f)}
  .vp-save{justify-self:start;background:var(--f-green,#0E7C61);color:#fff;border:0;border-radius:99px;padding:10px 20px;font-weight:700;font-size:13px;cursor:pointer}
  .vp-save:disabled{opacity:.6}
  .vp-toggle{display:flex!important;flex-direction:row!important;align-items:center;gap:10px;cursor:pointer}
  .vp-toggle input{width:18px;height:18px}
  .vp-toggle span{display:inline-flex;align-items:center;gap:7px;font-size:13.5px;font-weight:500;color:var(--f-ink,#1d1c16);text-transform:none;letter-spacing:0}
  .vp-share{display:grid;gap:12px;padding-top:6px;border-top:1px dashed var(--f-line,#eee)}
  .vp-link{font-size:12.5px;color:var(--f-green,#0E7C61);font-weight:600;text-decoration:none;word-break:break-all}
  .vp-contribs{list-style:none;margin:0;padding:0}
  .vp-contribs li{display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-top:1px solid #f0efe9}
  .vp-contribs li:first-child{border-top:0}
  .vp-contribs span{font-size:13.5px;font-weight:500}
  .vp-contribs small{font-size:11.5px;color:#8a887f;white-space:nowrap}
  .vp-empty{font-size:13px;color:var(--f-slate,#8a887f);font-style:italic;margin:0;line-height:1.55}
`
